import {ChangeEvent, HTMLAttributes, MouseEvent, useRef, useState} from 'react';
import styled from 'styled-components';
import {useBackgroundClick} from '@/hooks';
import {FlexFullGrow} from '@/styled-components';
import {interceptProps} from '@/styled-components/utils';
import {palette} from '@/styles';
import {Checkbox} from '../Checkbox';
import {Typography} from '../Typography';
import {ReactComponent as IconDown} from './expand-more.svg';

interface IOption {
  label: string;
  id: any;
}

interface IProps extends HTMLAttributes<HTMLDivElement> {
  label?: string;
  required?: boolean;
  placeholder?: string;
  activePlaceholder?: boolean; // placeholder값을 활성화 값처럼 인식함 (텍스트색상 진하게)
  list?: IOption[];
  selected?: IOption[];
  disabled?: boolean;
  readOnly?: boolean;
  fullWidth?: boolean;
  multi?: boolean;
  dropdownListStyle?: React.CSSProperties;
  onSelected?(selected: IOption[]): void;
}

export const Dropdown = ({
  label,
  required,
  placeholder,
  activePlaceholder,
  selected,
  list,
  disabled,
  readOnly,
  fullWidth,
  multi,
  dropdownListStyle,
  onSelected,
  ...props
}: IProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState<boolean>(false);

  const hasSelected = !!selected?.length;
  const hasList = !!list?.length;

  const handleClickText = (e: MouseEvent<HTMLDivElement>) => {
    if (readOnly) {
      return;
    }

    e.stopPropagation();

    if (!disabled) {
      setOpen((prev) => !prev);
    }
  };

  const handleSelected = (item: IOption) => {
    if (!disabled) {
      setOpen(false);
      onSelected?.([item]);
    }
  };

  const handleChecked = (e: ChangeEvent<HTMLInputElement>) => {
    if (!onSelected || !list) {
      return;
    }

    const {name, checked} = e.target;
    const prevSelected = selected || [];

    if (checked) {
      const item = list.find((i) => i.id === name);

      if (item) {
        onSelected([...prevSelected, item]);
      }
    } else {
      onSelected(prevSelected.filter((i) => i.id !== name));
    }
  };

  useBackgroundClick(
    listRef,
    () => {
      setOpen(false);
    },
    [selectorRef]
  );

  const hasLabel = !!label?.length;

  return (
    <Root fullWidth={fullWidth} hasLabel={hasLabel} {...props}>
      {hasLabel && (
        <Label>
          {label}
          {required && <Typography color={palette.red100}>*</Typography>}
        </Label>
      )}
      <Selector
        ref={selectorRef}
        active={open}
        disabled={disabled}
        onClick={handleClickText}
      >
        <Typography
          variant="medium16"
          color={
            disabled
              ? palette.disabled
              : hasSelected || activePlaceholder
              ? palette.textPrimary
              : palette.placeholder
          }
          ellipsis
        >
          {hasSelected ? selected.map((i) => i.label).join(', ') : placeholder}
        </Typography>
        <FlexFullGrow />
        <Arrow
          color={open ? palette.teal100 : palette.gray600}
          style={{opacity: disabled ? 0.2 : 1}}
        />
      </Selector>
      {open && hasList && (
        <List ref={listRef} style={dropdownListStyle}>
          {list.map((item) => {
            const active =
              hasSelected && selected.findIndex((i) => i.id === item.id) >= 0;
            return (
              <SelectListItem
                key={item.id}
                onClick={!multi ? () => handleSelected(item) : undefined}
              >
                <Checkbox
                  id={`checkbox-selected-list-${item.id}`}
                  checkType={multi ? 'circle' : 'radio'}
                  name={item.id}
                  checked={active}
                  variant="large"
                  onChange={handleChecked}
                />
                <Typography ellipsis>{item.label}</Typography>
              </SelectListItem>
            );
          })}
        </List>
      )}
    </Root>
  );
};

const Root = styled('div').withConfig({
  shouldForwardProp: interceptProps(['fullWidth', 'hasLabel']),
})<{
  fullWidth?: boolean;
  hasLabel?: boolean;
}>`
  position: relative;
  ${({fullWidth}) => fullWidth && `width: 100%;`}
  ${({hasLabel}) => hasLabel && `padding-top: 8px;`}
  gap: 4px;
  display: flex;
  flex-direction: column;
`;

const Arrow = styled(IconDown)`
  transition: all 0.3s;
  flex-shrink: 0;
  margin-left: 4px;
`;

const Selector = styled('div').withConfig({
  shouldForwardProp: interceptProps(['active', 'disabled']),
})<{
  active?: boolean;
  disabled?: boolean;
}>`
  padding: 9px 12px 9px 18px;
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${palette.gray200};
  background-color: ${palette.white};
  cursor: pointer;
  ${Typography} {
    flex-grow: 1;
  }
  ${({active}) =>
    active &&
    `
    ${Arrow} {
      transform: rotate(180deg);
    }
    border-color: ${palette.primary400};
  `}
  ${({disabled}) =>
    disabled &&
    `
    background-color: ${palette.gray100};
    border-color: ${palette.gray100};
    cursor: default;
  `}
`;

const List = styled.div`
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  width: 100%;
  max-height: 360px;
  overflow: auto;
  padding: 12px 16px;
  background-color: ${palette.white};
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  z-index: 1;
`;

const Label = styled.label`
  color: ${palette.gray800};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: -0.03px;
`;

const SelectListItem = styled(Label).attrs({
  as: 'div',
})`
  padding: 8px 8px 8px 0;
  border-radius: 8px;
  white-space: nowrap;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover {
    background-color: ${palette.gray100};
  }
`;
